import { type FC, useState } from 'react'
import pluralize from 'pluralize'
import { Col, Container, Row } from 'react-bootstrap'
import { Modal } from '../../components/Modal/Modal'
import { FormFeedback } from '../../components/Form/FormFeedback'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { type RoleType } from '../../models/Role'
import { type InviteResponse, usePromoteInvitesMutation } from '../../store/auth/auth'
import { selectAccountId } from '../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { addToast } from '../../store/toast/toastSlice'
import { readPromoteResult } from './promoteResult'
import { seatTypeLabels } from './roleChangeSeatImpact'

export interface AssignSeatModalProps {
  invite: InviteResponse
  seatType: RoleType
  freeSeats: number
  onHide: () => void
  onSeatsChanged: () => void
}

export const AssignSeatModal: FC<AssignSeatModalProps> = ({ invite, seatType, freeSeats, onHide, onSeatsChanged }) => {
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()
  const { trackingClient } = useTracking()
  const [promoteInvites, { isLoading }] = usePromoteInvitesMutation()
  const [error, setError] = useState<string | null>(null)

  const remaining = Math.max(freeSeats - 1, 0)

  const handleAssign = async () => {
    setError(null)
    let verdict
    try {
      const response = await promoteInvites({ accountId, invitationIds: [invite.id] }).unwrap()
      verdict = readPromoteResult(response, invite.id, seatType)
    } catch {
      verdict = readPromoteResult(undefined, invite.id, seatType)
    }

    if (verdict.refreshesSeats) {
      onSeatsChanged()
    }

    if (verdict.ok) {
      trackingClient.track({ eventName: TrackEvent.SEAT_ASSIGNED, eventDetails: { email: invite.user.email } })
      dispatch(addToast({ variant: 'success', title: 'Seat assigned. Invitation sent' }))
      onHide()
      return
    }

    if (verdict.closesModal) {
      onHide()
      return
    }

    setError(verdict.message ?? null)
  }

  const body = (
    <Container>
      <Row className='pb-3'>
        <Col>
          <h4 className='d-flex justify-content-center align-items-center'>Assign a seat to this user?</h4>
        </Col>
      </Row>
      <Row className='pb-4'>
        <Col className='p-0'>
          <div className='paragraph-medium'>
            This sends their invitation. You&apos;ll have {remaining} {seatTypeLabels[seatType]} {pluralize('seat', remaining)} left.
          </div>
          {error ? <FormFeedback>{error}</FormFeedback> : null}
        </Col>
      </Row>
    </Container>
  )

  return (
    <Modal
      show
      onHide={onHide}
      header={<></>}
      body={body}
      showFooterActions
      onFooterPrimaryAction={handleAssign}
      footerPrimaryActionLoading={isLoading}
      footerPrimaryActionTitle='Assign'
    />
  )
}
